import { CheckCircle as CheckCircleIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Avatar, Box, Chip, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { gateway } from "../../../gateway";
import { colors, roleColors } from "../../ui/colors";

const CHIP_BASE_SX = {
  fontWeight: 700,
  fontSize: "0.7rem",
  height: 24,
};

const chipSx = (active, activeBg, activeBorder, activeLabel) => ({
  ...CHIP_BASE_SX,
  bgcolor: active ? activeBg : colors.gray[50],
  border: `1px solid ${active ? activeBorder : colors.gray[300]}`,
  "& .MuiChip-label": { color: active ? activeLabel : colors.gray[600] },
});

const fetchJobStats = async (uid) => {
  const token = sessionStorage.getItem("token");
  const response = await gateway.get(`/jobs/self/workforce/${uid}?status=assigned`, {
    headers: { Authorization: token },
  });
  const items = response.data.data.items;
  return {
    assignedJobs: items.length,
    completedJobs: items.filter((item) => item.status === "completed").length,
  };
};

export const QuickPreview = ({ workforce, onDeleteClick, onClick, ...rest }) => {
  const [stats, setStats] = useState(null);
  const loading = stats === null;

  useEffect(() => {
    if (!workforce?.uid) return;
    fetchJobStats(workforce.uid)
      .then(setStats)
      .catch((error) => console.error("Error fetching job stats:", error));
  }, [workforce?.uid]);

  if (!workforce) return null;

  const roleColor = roleColors[workforce.workforceRole || workforce.role]?.base || colors.gray[400];

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onDeleteClick?.();
  };

  return (
    <Box onClick={onClick} {...rest} sx={{ p: 1.5, width: "100%" }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ position: "relative" }}>
        <Avatar src={workforce.avatar} sx={{ width: 44, height: 44, fontWeight: 600, bgcolor: colors.gray[50], color: roleColor }}>
          {!workforce.avatar && workforce.firstName?.charAt(0).toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.gray[900], lineHeight: 1.2, pl: 0.2, mb: 1.25 }} noWrap>
              {workforce.firstName} {workforce.lastName}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
            {loading ? (
              <>
                <Skeleton variant="rectangular" width={90} height={22} animation="wave" sx={{ borderRadius: 12 }} />
                <Skeleton variant="rectangular" width={110} height={22} animation="wave" sx={{ borderRadius: 12 }} />
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Chip
                  label={`Assigned: ${stats.assignedJobs}`}
                  size="small"
                  sx={chipSx(stats.assignedJobs > 0, colors.orange[100], colors.orange[300], colors.orange[800])}
                />
                <Chip
                  label={`Completed: ${stats.completedJobs}`}
                  size="small"
                  icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    ...chipSx(stats.completedJobs > 0, roleColors.residential[100], roleColors.residential[400], roleColors.residential[800]),
                    "& .MuiChip-icon": {
                      color: stats.completedJobs > 0 ? roleColors.residential[700] : colors.gray[500],
                    },
                  }}
                />
              </Box>
            )}
          </Stack>
        </Box>

        <IconButton
          size="small"
          onClick={handleDeleteClick}
          aria-label="delete workforce"
          sx={{
            color: colors.gray[500],
            position: { xs: "absolute", sm: "static" },
            top: { xs: -5, sm: "auto" },
            right: { xs: 8, sm: "auto" },
            "&:hover": {
              color: colors.red?.[600] || "#d32f2f",
              backgroundColor: colors.red?.[50] || "rgba(211,47,47,0.08)",
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
};
